import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { Logo } from "@/components/ui/Logo";
import { ArrowLeft, Clock, RefreshCw } from "lucide-react";

interface LocationState {
  userId: string;
  email: string;
  type: "forgot-password";
}

export function VerifyOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme } = useTheme();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const state = location.state as LocationState;
  const { userId, email } = state || {};

  useEffect(() => {
    if (!userId || !email) {
      navigate("/forgot-password");
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [userId, email, navigate]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill("")]);
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setIsLoading(true);
    try {
      // verifyType: 2 is for forget_password (from backend enum)
      const response = await apiService.verifyOTP(otpString, userId, 2);
      if (response.type === 0) {
        toast.success("Code verified successfully!");
        navigate("/reset-password", { state: { userId, email } });
      } else {
        toast.error(response.message || "Invalid verification code");
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Verification failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      const response = await apiService.resendOTP(userId);
      if (response.type === 0) {
        toast.success("New code sent to your email!");
        setCountdown(60);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        toast.error(response.message || "Failed to resend code");
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to resend code. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Back to Forgot Password */}
        <div className="mb-6">
          <Link
            to="/forgot-password"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forgot Password
          </Link>
        </div>

        {/* OTP Verification Card */}
        <Card className="border-2 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4">
              <Logo size={48} showText={false} />
            </div>
            <CardTitle className="text-2xl font-bold">Verify Code</CardTitle>
            <CardDescription>Enter the 6-digit code sent to your email</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Email Display */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Code sent to:</p>
                <p className="font-medium text-foreground">{email}</p>
              </div>

              {/* OTP Input */}
              <div className="space-y-4">
                <Label>Enter Verification Code</Label>
                <div className="flex gap-2 justify-center">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => {
                        handleOtpChange(index, e.target.value);
                      }}
                      onKeyDown={(e) => {
                        handleKeyDown(index, e);
                      }}
                      onPaste={handlePaste}
                      className="w-12 h-12 text-center text-lg font-semibold"
                      style={{
                        borderColor: digit ? currentTheme.colors.primary : undefined,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Verify Button */}
              <Button
                onClick={handleVerifyOTP}
                className="w-full"
                disabled={!isOtpComplete || isLoading}
                style={{ backgroundColor: currentTheme.colors.primary }}
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </Button>

              {/* Resend Section */}
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">Didn't receive the code?</p>

                {canResend ? (
                  <Button
                    variant="link"
                    onClick={handleResendOTP}
                    disabled={isResending}
                    className="p-0 h-auto text-sm"
                    style={{ color: currentTheme.colors.primary }}
                  >
                    {isResending ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Resend Code"
                    )}
                  </Button>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Resend available in {countdown}s</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Need help? Contact support</p>
        </div>
      </div>
    </div>
  );
}
